import React, { useMemo } from 'react';
import { getPalettesByFamily } from '../../../constants/genArt';
import { hexToRgb } from '../../../constants/colors';

/**
 * Returns true if a hex color is near-white or near-black.
 * Used to filter palette swatches to only chromatic colors.
 */
function isWhiteOrBlack(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return false;
  const lum = 0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b;
  return lum > 230 || lum < 18;
}

/**
 * Pick exactly 4 display colors from a palette:
 * Filter out white/black, then take 4 evenly spaced from what remains.
 */
function pickDisplayColors(pal) {
  const allColors = [pal.bg, ...pal.colors];
  const chromatic = allColors.filter(c => !isWhiteOrBlack(c));

  if (chromatic.length <= 4) {
    // Pad with remaining colors if fewer than 4 chromatic
    const padded = [...chromatic];
    for (const c of allColors) {
      if (padded.length >= 4) break;
      if (!padded.includes(c)) padded.push(c);
    }
    return padded.slice(0, 4);
  }

  // Pick 4 evenly spaced
  const step = (chromatic.length - 1) / 3;
  return [0, 1, 2, 3].map(i => chromatic[Math.round(i * step)]);
}

/**
 * Palette picker — recessed square instrument buttons in a 4-column grid.
 * Each button shows a 2×2 color swatch grid (same pattern as Saved Palettes)
 * plus name, with LED indicator on active.
 */
export function GenArtPalettePicker({ family, selectedPaletteId, onSelect }) {
  const palettes = useMemo(() => getPalettesByFamily(family), [family]);

  return (
    <div>
      <div className="text-[10px] text-white/40 mb-1.5 uppercase tracking-wider">Palette</div>
      <div className="genart-palette-grid">
        {palettes.map(pal => {
          const isActive = pal.id === selectedPaletteId;
          const displayColors = pickDisplayColors(pal);

          // Accent color for LED glow
          const accentHex = pal.accent || pal.colors[Math.floor(pal.colors.length / 2)] || pal.bg;
          const accentRgb = hexToRgb(accentHex);
          const ledGlow = accentRgb
            ? `rgba(${accentRgb.r},${accentRgb.g},${accentRgb.b},0.6)`
            : 'rgba(52,211,153,0.6)';

          return (
            <button
              key={pal.id}
              className={`genart-palette-btn${isActive ? ' genart-palette-btn-active' : ''}`}
              onClick={() => onSelect(pal.id)}
              title={pal.name}
            >
              {/* LED dot */}
              <div
                className={`genart-palette-led${isActive ? ' genart-palette-led-on' : ''}`}
                style={isActive ? {
                  background: accentHex,
                  '--pal-led-glow': ledGlow,
                } : undefined}
              />

              {/* 2×2 color swatch grid — same as Saved Palettes */}
              <div className="color-swatch-grid">
                {displayColors.map((color, i) => (
                  <span
                    key={i}
                    className="color-swatch-cell"
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>

              {/* Name */}
              <span
                className="genart-palette-name"
                style={isActive ? { color: accentHex } : undefined}
              >
                {pal.name}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
